import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LandlordDashboard from "../../components/LandLord/LandlordDashboard";
import AddPropertyModal from "../../components/LandLord/AddPropertyModel";
import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";
import { useConfirmation } from "../../hooks/useConfirmation";
import ConfirmationModal from "../../components/Model/ConfirmationModel";
import NotFound from "../../components/NotFound";
import { useThunk } from "../../hooks/useThunk";
import { fetchUserProperties, createProperty, editProperty, deleteProperty } from "../../store/thunks/propertyThunks";

export default function ManageLands() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastSubmittedSearch = searchParams.get("search") || "";
  //const isError = false; // for testing - if error this is true

  const [search, setSearch] = useState(lastSubmittedSearch); // input value only (doesn't trigger API)
  const [showTable, setShowTable] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const navigate = useNavigate();

  const [doFetchUserProperties, isFetchUserProperties, fetchUserPropertiesError] = useThunk(fetchUserProperties);
  const [doCreateProperty, isCreatingProperty, createPropertyError] = useThunk(createProperty);
  const [doEditProperty, isEditingProperty, editPropertyError] = useThunk(editProperty);
  const [doDeleteProperty, isDeleteProperty, deletePropertyError] = useThunk(deleteProperty);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");
  const [propertyData, setPropertyData] = useState([]);
  const [propertySummaryData, setPropertySummaryData] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalProperties, setTotalProperties] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [totalRentCollected, setTotalRentCollected] = useState(0);
  const [totalRentExpected, setTotalRentExpected] = useState(0);
  const [totalVacant, setTotalVacant] = useState(0);
  const [totalOccupied, setTotalOccupied] = useState(0);

  const isError = !!fetchUserPropertiesError;
  const isLoading = isFetchUserProperties;
  const isSearching = (lastSubmittedSearch || "").trim().length > 0;
  const showNoResults = !isLoading && isSearching && totalRecords === 0;
  const showNoPropsYet = !isLoading && !isSearching && totalRecords === 0;
  
  const fetchProperties = useCallback(async (page = 1, pageSize = 6, searchQuery = lastSubmittedSearch) => {
    const offset = (page - 1) * pageSize;
    const result = await doFetchUserProperties({ offset, limit: pageSize, search: searchQuery.trim(), });
    if(result?.success) {
      // console.log('fetched property data', result?.response);
      setPropertyData(result?.response?.data?.data || []);
      setPropertySummaryData(result?.response?.data?.rentSummeries || []);
      setTotalRecords(result?.response?.data?.pagination?.totalDocuments || 0);
      // console.log('total records', totalRecords);
      // console.log('summary data', result?.response?.data?.rentSummeries);
    }
  },
    [doFetchUserProperties, lastSubmittedSearch]
  );

  useEffect(() => {
    fetchProperties(1, 6, lastSubmittedSearch);
  }, [fetchProperties, lastSubmittedSearch]);

  // SEARCH button (and Enter key) -> update URL + fetch
  const handleSearchSubmit = useCallback(() => {
    const value = (search || "").trim();
    setSearchParams(value ? { search: value } : {});
    fetchProperties(1, 6, value);
  }, [fetchProperties, search, setSearchParams]);

  // Optional: support pressing Enter in the search field
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    setSearchParams({});
    fetchProperties(1, 6, "");
  };

  // importing the custom hook properties
  const { requestConfirmation, confirmation, confirm, cancel } = useConfirmation();

  const handleViewUnits = (property) => {
    setSelectedProperty(property);
    setShowTable(true);
    navigate(`/landlords/manage-property/view-details/${property._id}`);
  };

  const handleBackToCards = () => {
    setShowTable(false);
    setSelectedProperty(null);
  };

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setModalMode("edit");
    setShowModal(true);
  };

  const handleModalClose = () => {
    setSelectedProperty(null);
    setShowModal(false);
  };

  const handleModalSubmit = () => {
    handleModalClose();
    fetchProperties(1, 6, lastSubmittedSearch);
  };

  // const handleSearch = () => {
  //   // console.log("Searching:", {
  //   //   search,
  //   // });
  //       setSearchParams({ search });

  //       const filteredProperties = propertyData.filter(property =>
  //         property.propertyName.toLowerCase().includes(search.toLowerCase())
  //       );
    
  //       let totalVacant = 0;
  //       let totalOccupied = 0;
  //       let totalUnits = 0;
      
  //       filteredProperties.forEach(property => {
  //   totalVacant += property.vacant;
  //   totalOccupied += property.occupied;
  //   totalUnits += property.totalUnits;
  // });
  // setTotalProperties(filteredProperties.length);
  // setTotalUnits(totalUnits);
  // };

  const handleDelete = async (property) => {
    // populating the model and await until the user select one of it's options
    const isConfirmed = await requestConfirmation({
      type: "delete",
      title: "Delete Property",
      message:
        "Are you sure you want to delete this property? This will remove all associated units.",
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    // continue or terminating the rest flow of the method based on user selections of the model
    if (isConfirmed) {
      // console.log('selected property delete model', property._id)
      const result  = await doDeleteProperty({propertyId: property._id});
      if(result?.success) {
        fetchProperties(1, 6, lastSubmittedSearch);
      }
    } else {
      // terminating the flow
      // console.log("Delete operation canceled.");
    }
  };

  const fetchWithPagination = (page, pageSize) => {
    fetchProperties(page, pageSize, lastSubmittedSearch);
  };

  const isEmptyData = !isFetchUserProperties && totalRecords === 0;

  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <div className="flex-row items-center justify-center w-full h-screen">
        {!showTable && (
          <div className="">
            <TableHeader
              searchValue={search}
              onSearchChange={setSearch}
              onSearchSubmit={handleSearchSubmit}
              onSearchKeyDown={handleSearchKeyDown}
              searchPlaceholder="Search by property name"
              buttons={[
                {
                  label: "SEARCH",
                  onClick: handleSearchSubmit,
                  variant: "main",
                },
              ]}
              isAddProperty={true}
              onAddPropertyClick={() => {
                setModalMode("add");
                setShowModal(true);
              }}
              searchWrapperClass="w-full [@media(min-width:585px)]:w-auto"
              containerClass="md:grid md:grid-cols-3 flex flex-wrap sm:flex-nowrap"
              buttonWrapperClass="w-full xs:w-auto"
            />
          </div>
        )}

          <div>
            <div className="my-4 sm:my-6 md:my-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F]">
              {/* Stat Cards */}
              <div className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <InfoCard
                    label="Rent Collected (UGX)"
                    value={totalRentCollected > 0 ? totalRentCollected : propertySummaryData?.totalRentCollect || 0}
                    className=" max-h-[120px] mx-auto overflow-hidden"
                    labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                    valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                    popoverTextStyle="text-level-4"
                  />
                </div>
                <div>
                  <InfoCard
                    label="Rent Expected (UGX)"
                    value={totalRentExpected > 0 ? totalRentExpected : propertySummaryData?.totalThisMonthRentCollect || 0}
                    className=" max-h-[120px] mx-auto overflow-hidden"
                    labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                    valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                    popoverTextStyle="text-level-4"
                  />
                </div>
                <div>
                  <InfoCard
                    label="Properties"
                    value={totalProperties > 0 ? totalProperties : propertySummaryData?.totalNumberProperties || 0}
                    className=" max-h-[120px] mx-auto"
                    labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                    valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                    popoverTextStyle="text-level-4"
                  />
                </div>
                <div>
                  <InfoCard
                    label="Units"
                    value={totalUnits > 0 ? totalUnits : propertySummaryData?.totalNumberOfUnits || 0}
                    className=" max-h-[120px] mx-auto"
                    labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
                    valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
                    popoverTextStyle="text-level-4"
                  />
                </div>
              </div>
            </div>
       {showNoPropsYet ? (
          <NotFound
            text1="No properties yet"
            text2="You haven't added any properties yet."
            isButton={true}
            buttonText="Add your first property"
            onClick={() => {
              setModalMode("add");
              setShowModal(true);
            }}
          />
        ) : isError ? (
          <NotFound
            text1="Something went wrong"
            text2="We couldn’t load your properties. Please try again."
            isButton={true}
            buttonText="Retry"
            onClick={() => fetchProperties(1, 6, lastSubmittedSearch)}
          />
        ) : showNoResults ? (
          <NotFound
            text1="No properties found"
            text2="No properties found matching your search."
            isButton={true}
            buttonText="Clear search"
            onClick={handleClearSearch}
          />
        ) : ( 
            <div className="">
              <LandlordDashboard
                properties={propertyData}
                showTable={showTable}
                selectedProperty={selectedProperty}
                onViewUnits={handleViewUnits}
                onBackToCards={handleBackToCards}
                onEditProperty={handleEditProperty}
                onDeletePropertyCard={handleDelete}
                totalRecords={totalRecords}
                fetchProperties={fetchWithPagination}
                search={lastSubmittedSearch}
              />
            </div>
            )}
          </div>

        {showModal && (
          <AddPropertyModal
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
            mode={modalMode}
            defaultData={selectedProperty}
            doEditProperty={doEditProperty}
            doCreateProperty={doCreateProperty}
            isEditingProperty={isEditingProperty}
            isCreatingProperty={isCreatingProperty}
          />
        )}
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmation.isOpen}
          options={confirmation.options}
          onConfirm={confirm}
          onCancel={cancel}
        />
      </div>
    </div>
  );
}
